import { flexRender, type Table } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { type KeyboardEvent, useEffect, useRef } from 'react';
import { cssVars, cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import { Skeleton } from '../Skeleton';
import s from './DataTable.module.css';

const ROW_HEIGHT = 44;
/** Below this many rows everything renders (simpler DOM, works for print and find-in-page). */
const VIRTUALIZE_FROM = 80;

export interface TableViewProps<T> {
  table: Table<T>;
  caption: string;
  loading?: boolean;
  onRowClick?: (row: T) => void;
  activeRowId?: string;
  hasMore?: boolean;
  onLoadMore?: () => void;
  minWidth: number;
}

export function TableView<T>({ table, caption, loading, onRowClick, activeRowId, hasMore, onLoadMore, minWidth }: TableViewProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const rows = table.getRowModel().rows;
  const virtual = rows.length >= VIRTUALIZE_FROM;
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual is used as documented
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });
  const items = virtual ? virtualizer.getVirtualItems() : null;
  const top = items?.[0]?.start ?? 0;
  const bottom = items ? virtualizer.getTotalSize() - (items[items.length - 1]?.end ?? 0) : 0;
  const visible = items ? items.map((i) => rows[i.index]).filter((r) => r !== undefined) : rows;
  const leaf = table.getVisibleLeafColumns();

  // Keyset paging: ask for the next page when the end of the list comes into view.
  const lastIndex = items ? (items[items.length - 1]?.index ?? 0) : rows.length - 1;
  useEffect(() => {
    if (hasMore && onLoadMore && virtual && lastIndex >= rows.length - 10) onLoadMore();
  }, [hasMore, onLoadMore, virtual, lastIndex, rows.length]);

  const onKey = (e: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    if (e.key === 'Enter' && onRowClick) {
      e.preventDefault();
      onRowClick(row);
    }
  };

  return (
    <div ref={scrollRef} className={s.scroll}>
      <table className={s.table} style={cssVars({ '--dt-min-w': `${minWidth}px` })}>
        <caption className="sr-only">{caption}</caption>
        <colgroup>
          {leaf.map((c) => (
            <col key={c.id} className={s.col} style={cssVars({ '--col-w': c.columnDef.meta?.width })} />
          ))}
        </colgroup>
        <thead className={s.thead}>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => {
                const sorted = h.column.getIsSorted();
                const canSort = h.column.getCanSort();
                const align = h.column.columnDef.meta?.align;
                return (
                  <th
                    key={h.id}
                    scope="col"
                    className={cx(s.th, align === 'end' && s.end)}
                    aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined}
                  >
                    {canSort ? (
                      <button type="button" className={s.sortButton} onClick={h.column.getToggleSortingHandler()}>
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {sorted === 'asc' ? (
                          <ArrowUp aria-hidden />
                        ) : sorted === 'desc' ? (
                          <ArrowDown aria-hidden />
                        ) : (
                          <ArrowUpDown className={s.sortIdle} aria-hidden />
                        )}
                      </button>
                    ) : (
                      flexRender(h.column.columnDef.header, h.getContext())
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {loading &&
            Array.from({ length: 8 }, (_, i) => (
              <tr key={`sk${i}`} className={s.tr}>
                {leaf.map((c) => (
                  <td key={c.id} className={s.td}>
                    <Skeleton width={c.id === '_select' ? '16px' : '70%'} height="12px" />
                  </td>
                ))}
              </tr>
            ))}
          {!loading && top > 0 && (
            <tr className={s.spacer} style={cssVars({ '--h': `${top}px` })} aria-hidden>
              <td colSpan={leaf.length} />
            </tr>
          )}
          {!loading &&
            visible.map((row) => (
              <tr
                key={row.id}
                className={cx(s.tr, onRowClick && s.clickable, row.getIsSelected() && s.selected, row.id === activeRowId && s.active)}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                onKeyDown={onRowClick ? (e) => onKey(e, row.original) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                aria-selected={row.getIsSelected() || row.id === activeRowId || undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={cx(s.td, cell.column.columnDef.meta?.align === 'end' && s.end)}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          {!loading && bottom > 0 && (
            <tr className={s.spacer} style={cssVars({ '--h': `${bottom}px` })} aria-hidden>
              <td colSpan={leaf.length} />
            </tr>
          )}
          {!loading && hasMore && onLoadMore && !virtual && (
            <tr>
              <td colSpan={leaf.length} className={s.moreCell}>
                <button type="button" className={s.moreButton} onClick={onLoadMore}>
                  {t('Load more')}
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
